import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Tag,
  Space,
  Modal,
  DatePicker,
  Form,
  message,
  Badge,
  Avatar,
  Tooltip,
  Row,
  Col,
  Statistic,
  Alert,
  Empty,
  Select,
} from 'antd';
import {
  CalendarOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  NotificationOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { vipService, VIPParking, VIPUpdateData } from '../../../services/vipService';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ExpiringVIPsProps {
  refreshKey: number;
  onDataChange: () => void;
}

interface ExpiringStats {
  expiring1Day: number;
  expiring3Days: number;
  expiring7Days: number;
  expiring30Days: number;
}

// 樣式函數
const getUrgencyStyle = (urgency: string): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    borderLeft: '4px solid',
  };

  switch (urgency) {
    case 'critical':
      return {
        ...baseStyle,
        borderLeftColor: '#ff4d4f',
        backgroundColor: '#fff2f0',
      };
    case 'high':
      return {
        ...baseStyle,
        borderLeftColor: '#fa8c16',
        backgroundColor: '#fff7e6',
      };
    case 'medium':
      return {
        ...baseStyle,
        borderLeftColor: '#faad14',
        backgroundColor: '#fffbe6',
      };
    case 'expired':
      return {
        ...baseStyle,
        borderLeftColor: '#ff4d4f',
        backgroundColor: '#fff2f0',
        opacity: 0.8,
      };
    default:
      return baseStyle;
  }
};

const ExpiringVIPs: React.FC<ExpiringVIPsProps> = ({ refreshKey, onDataChange }) => {
  const [loading, setLoading] = useState(false);
  const [expiringVIPs, setExpiringVIPs] = useState<VIPParking[]>([]);
  const [stats, setStats] = useState<ExpiringStats>({
    expiring1Day: 0,
    expiring3Days: 0,
    expiring7Days: 0,
    expiring30Days: 0,
  });
  const [selectedVIP, setSelectedVIP] = useState<VIPParking | null>(null);
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [renewForm] = Form.useForm();
  const [renewLoading, setRenewLoading] = useState(false);
  const [daysFilter, setDaysFilter] = useState(7);

  useEffect(() => {
    loadExpiringVIPs();
    loadStats();
  }, [refreshKey, daysFilter]);

  const loadExpiringVIPs = async () => {
    try {
      setLoading(true);
      const response = await vipService.getExpiringVIPs(daysFilter);
      setExpiringVIPs(response.data);
    } catch (error) {
      console.error('載入即將到期VIP失敗:', error);
      message.error('載入即將到期VIP失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [
        expiring1Day,
        expiring3Days,
        expiring7Days,
        expiring30Days,
      ] = await Promise.all([
        vipService.getExpiringVIPs(1),
        vipService.getExpiringVIPs(3),
        vipService.getExpiringVIPs(7),
        vipService.getExpiringVIPs(30),
      ]);

      setStats({
        expiring1Day: expiring1Day.count,
        expiring3Days: expiring3Days.count,
        expiring7Days: expiring7Days.count,
        expiring30Days: expiring30Days.count,
      });
    } catch (error) {
      console.error('載入到期統計失敗:', error);
    }
  };

  const getDaysLeft = (validTo: string) => {
    const end = dayjs(validTo);
    const now = dayjs();
    return end.diff(now, 'day');
  };

  const getDaysLeftTag = (validTo: string) => {
    const daysLeft = getDaysLeft(validTo);
    
    if (daysLeft < 0) {
      return <Tag color="red">已過期 {Math.abs(daysLeft)} 天</Tag>;
    } else if (daysLeft === 0) {
      return <Tag color="red">今天到期</Tag>;
    } else if (daysLeft === 1) {
      return <Tag color="orange">明天到期</Tag>;
    } else if (daysLeft <= 3) {
      return <Tag color="orange">{daysLeft} 天後到期</Tag>;
    } else if (daysLeft <= 7) {
      return <Tag color="yellow">{daysLeft} 天後到期</Tag>;
    } else {
      return <Tag color="blue">{daysLeft} 天後到期</Tag>;
    }
  };

  const getUrgencyLevel = (validTo: string) => {
    const daysLeft = getDaysLeft(validTo);
    if (daysLeft < 0) return 'expired';
    if (daysLeft <= 1) return 'critical';
    if (daysLeft <= 3) return 'high';
    if (daysLeft <= 7) return 'medium';
    return 'low';
  };

  const handleRenew = (vip: VIPParking) => {
    setSelectedVIP(vip);
    renewForm.setFieldsValue({
      currentEndDate: dayjs(vip.validTo),
      newDateRange: [
        dayjs(vip.validTo),
        dayjs(vip.validTo).add(1, 'year'),
      ],
    });
    setRenewModalVisible(true);
  };

  const handleRenewSubmit = async (values: any) => {
    if (!selectedVIP) return;

    try {
      setRenewLoading(true);
      
      const updateData: VIPUpdateData = {
        validTo: values.newDateRange[1].format('YYYY-MM-DD'),
        status: 'active' as const,
      };

      await vipService.updateVIPParking(selectedVIP._id, updateData);
      message.success('VIP車位續期成功');
      
      setRenewModalVisible(false);
      renewForm.resetFields();
      setSelectedVIP(null);
      onDataChange();
      loadExpiringVIPs();
      loadStats();
    } catch (error: any) {
      console.error('VIP續期失敗:', error);
      message.error(error.response?.data?.message || 'VIP續期失敗');
    } finally {
      setRenewLoading(false);
    }
  };

  const handleSendNotification = async (vip: VIPParking) => {
    try {
      // 這裡可以調用發送通知的API
      message.success(`已向 ${vip.owner.name} 發送到期提醒通知`);
    } catch (error) {
      message.error('發送通知失敗');
    }
  };

  const getAlertType = () => {
    if (stats.expiring1Day > 0) return 'error';
    if (stats.expiring3Days > 0) return 'warning';
    if (stats.expiring7Days > 0) return 'info';
    return 'success';
  };

  const getAlertMessage = () => {
    if (stats.expiring1Day > 0) {
      return `緊急提醒：有 ${stats.expiring1Day} 個VIP車位將在1天內到期！`;
    }
    if (stats.expiring3Days > 0) {
      return `注意：有 ${stats.expiring3Days} 個VIP車位將在3天內到期`;
    }
    if (stats.expiring7Days > 0) {
      return `提醒：有 ${stats.expiring7Days} 個VIP車位將在7天內到期`;
    }
    return '目前沒有即將到期的VIP車位';
  };

  return (
    <div className="expiring-vips">
      {/* 統計概覽 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6} md={6} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="1天內到期"
              value={stats.expiring1Day}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="3天內到期"
              value={stats.expiring3Days}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="7天內到期"
              value={stats.expiring7Days}
              valueStyle={{ color: '#faad14' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6} lg={6}>
          <Card className="stat-card">
            <Statistic
              title="30天內到期"
              value={stats.expiring30Days}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 提醒信息 */}
      <Alert
        message={getAlertMessage()}
        type={getAlertType()}
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 篩選和操作 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Space>
              <span>顯示範圍：</span>
              <Select
                value={daysFilter}
                onChange={setDaysFilter}
                style={{ width: 120 }}
              >
                <Option value={1}>1天內</Option>
                <Option value={3}>3天內</Option>
                <Option value={7}>7天內</Option>
                <Option value={15}>15天內</Option>
                <Option value={30}>30天內</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadExpiringVIPs();
                loadStats();
              }}
              loading={loading}
            >
              刷新數據
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 即將到期的VIP列表 */}
      <Card title={`即將到期的VIP車位 (${daysFilter}天內)`}>
        {expiringVIPs.length === 0 ? (
          <Empty
            description={`沒有在${daysFilter}天內到期的VIP車位`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={expiringVIPs}
            loading={loading}
            renderItem={(vip) => {
              const urgency = getUrgencyLevel(vip.validTo);
        const daysLeft = getDaysLeft(vip.validTo);
              
              return (
                <List.Item
                  style={getUrgencyStyle(urgency)}
                  actions={[
                    <Tooltip title="發送提醒">
                      <Button
                        type="text"
                        icon={<NotificationOutlined />}
                        onClick={() => handleSendNotification(vip)}
                      />
                    </Tooltip>,
                    <Tooltip title="續期">
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => handleRenew(vip)}
                      >
                        續期
                      </Button>
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge
                        count={daysLeft < 0 ? '過期' : daysLeft}
                        style={{
                          backgroundColor: daysLeft < 0 ? '#ff4d4f' : 
                                         daysLeft <= 1 ? '#ff4d4f' :
                                         daysLeft <= 3 ? '#fa8c16' :
                                         daysLeft <= 7 ? '#faad14' : '#1890ff'
                        }}
                      >
                        <Avatar
                          icon={<UserOutlined />}
                          style={{
                            backgroundColor: urgency === 'critical' ? '#ff4d4f' :
                                           urgency === 'high' ? '#fa8c16' :
                                           urgency === 'medium' ? '#faad14' : '#1890ff'
                          }}
                        />
                      </Badge>
                    }
                    title={
                      <Space>
                        <span style={{ fontWeight: 'bold' }}>
                          {vip.owner.name}
                        </span>
                        <Tag color="blue">{vip.parkingSpot.spotNumber}</Tag>
                        {getDaysLeftTag(vip.validTo)}
                      </Space>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <PhoneOutlined style={{ marginRight: 4 }} />
                          {vip.owner.phone}
                          <MailOutlined style={{ marginLeft: 12, marginRight: 4 }} />
                          {vip.owner.email}
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <CarOutlined style={{ marginRight: 4 }} />
                          車輛數量：{vip.vehicles.length}
                          <CalendarOutlined style={{ marginLeft: 12, marginRight: 4 }} />
                          到期日期：{dayjs(vip.validTo).format('YYYY-MM-DD')}
                        </div>
                        <div>
                          VIP等級：
                          <Tag color={
                            vip.vipLevel === 'platinum' ? 'purple' :
                            vip.vipLevel === 'gold' ? 'gold' : 'default'
                          }>
                            {vip.vipLevel === 'platinum' ? '白金會員' :
                             vip.vipLevel === 'gold' ? '黃金會員' : '銀牌會員'}
                          </Tag>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* 續期模態框 */}
      <Modal
        title="VIP車位續期"
        open={renewModalVisible}
        onCancel={() => setRenewModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedVIP && (
          <div>
            <Alert
              message={`為 ${selectedVIP.owner.name} 的VIP車位 ${selectedVIP.parkingSpot.spotNumber} 辦理續期`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Form
              form={renewForm}
              layout="vertical"
              onFinish={handleRenewSubmit}
            >
              <Form.Item
                name="currentEndDate"
                label="當前到期日期"
              >
                <DatePicker
                  disabled
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
              
              <Form.Item
                name="newDateRange"
                label="新的有效期"
                rules={[{ required: true, message: '請選擇新的有效期' }]}
              >
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['開始日期', '結束日期']}
                  disabledDate={(current) => {
                    // 不能選擇過去的日期
                    return current && current < dayjs().startOf('day');
                  }}
                />
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={renewLoading}
                    icon={<PlusOutlined />}
                  >
                    確認續期
                  </Button>
                  <Button onClick={() => setRenewModalVisible(false)}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExpiringVIPs;